import { ISectionLogger } from "@multiweb/logging";
import { ResultMessageType } from "@multiweb/types/contracts";
import {
    CryptoExtensionUnavailableException,
    EInsufficientVersionException,
    NativeAppBulkException,
    NativeAppException,
    NativeAppExitedException,
    NativeAppNoCertificateFoundException,
    NativeAppSelectionParamsException,
    NativeAppTooManyCertificatesFoundException,
    NativeAppUnavailableException,
} from "Cryptography/Crypto/CryptoExceptions";
import { ConditionErrorType, DataSourceException, InvalidArgumentException, InvalidConditionException, InvalidOperationException, UserAbortedException } from "Global/Base/Exceptions";
import { CancelError } from "Global/Base/Promises/CancellationTokenSource";
import { IDBException } from "IndexedDB/Promises/IndexedDBExceptions";
import {
    EDMSException,
    EOWDmsUnavailable,
    EOWException,
    EOWFormDefinitionNotFoundException,
    EOWKillSession,
    EOWLocalizableException,
    EOWLogonError,
    EOWNoFormNameException,
    EOWNoImplicitFormForClassException,
    EOWNoRightsToForm,
    EOWRecordPermissionException,
    EOWRunMethodInsufficientRightsException,
    EOWSessionTimeOut,
    EOWUnauthorizedAccess,
    EOWUnknownException,
    EOWUserException,
    EOWValidateException,
    EReadOnlyAttribute,
    EReadOnlyMethod,
} from "OW.Remoting/Basic/OWExceptions";
import { Localization } from "OW.Remoting/Remoting/Localization";
import { CryptoExtensionUnavailableBuilder } from "UI/UI/UI/DataBuilders/CryptoExtensionUnavailableBuilder";
import { CryptoNativeAppUnavailableBuilder } from "UI/UI/UI/DataBuilders/CryptoNativeAppUnavailableBuilder";
import { CryptoVersionInfoBuilder } from "UI/UI/UI/DataBuilders/CryptoVersionInfoBuilder";
import { LocalizedStringBuilder } from "UI/UI/UI/DataBuilders/LocalizedStringBuilder";
import { StringBuilder } from "UI/UI/UI/DataBuilders/StringBuilder";
import { IUserMsgsData, UserMsgsBuilder, UserMsgsData } from "UI/UI/UI/DataBuilders/UserMsgsBuilder";
import { MessageButtonType, MessageType, MessageWindow } from "UI/UI/UI/MessageWindow";
import { IUIManagerWorker, MessageButtonResult } from "UI/UI/UI/UIManager";
import {
    LOC_KEY_ERROR_CLEAR_AND_RETRY,
    LOC_KEY_ERROR_CNA_EXITED,
    LOC_KEY_ERROR_CNA_NO_CERTIFICATE_FOUND,
    LOC_KEY_ERROR_CNA_SELECTIONPARAMS,
    LOC_KEY_ERROR_CNA_TOO_MANY_CERTIFICATES_FOUND,
    LOC_KEY_ERROR_CONDITION_NOT_MET,
    LOC_KEY_ERROR_CONTACT_ADMIN,
    LOC_KEY_ERROR_EVENT_ID,
    LOC_KEY_ERROR_FILES_UNAVAILABLE,
    LOC_KEY_ERROR_FORM_DEFINITION_NOT_FOUND,
    LOC_KEY_ERROR_GENERIC_FAIL,
    LOC_KEY_ERROR_GENERIC_FAIL_RETRY,
    LOC_KEY_ERROR_GENERIC_RETRY,
    LOC_KEY_ERROR_GENERIC_UNABLE,
    LOC_KEY_ERROR_INDEXED_BD,
    LOC_KEY_ERROR_INSUFFICIENT_RIGHTS,
    LOC_KEY_ERROR_INSUFFICIENT_RIGHTS_RUN_ACTION,
    LOC_KEY_ERROR_INVALID_EXTENDED_FILTER,
    LOC_KEY_ERROR_INVALID_EXTENDED_FILTER_VALUES,
    LOC_KEY_ERROR_INVALID_FILTER,
    LOC_KEY_ERROR_INVALID_FILTER_ATTRIBUTE,
    LOC_KEY_ERROR_LOGGED_OUT,
    LOC_KEY_ERROR_LOGOUT,
    LOC_KEY_ERROR_MISSING_CNA_APP,
    LOC_KEY_ERROR_MISSING_CWE_EXTENSION,
    LOC_KEY_ERROR_MISSING_FORM_NAME,
    LOC_KEY_ERROR_NO_ASSOCIATED_FORM,
    LOC_KEY_ERROR_NO_DATA,
    LOC_KEY_ERROR_NO_RIGHTS_FORM,
    LOC_KEY_ERROR_NOTICE,
    LOC_KEY_ERROR_RELOG_AND_RETRY,
    LOC_KEY_ERROR_SECURITY_TOKEN_EXPIRED,
    LOC_KEY_ERROR_SERVER_UNAVAILABLE,
    LOC_KEY_ERROR_STORAGE_ERROR,
    LOC_KEY_ERROR_STORAGE_UNAVAILABLE,
    LOC_KEY_ERROR_UNABLE_TO_CREATE_DIGITAL_SIGNATURE,
    LOC_KEY_ERROR_UNABLE_TO_MODIFY,
    LOC_KEY_ERROR_UNABLE_TO_OPEN_FORM,
    LOC_KEY_ERROR_UNABLE_TO_RUN_ACTION,
    LOC_KEY_ERROR_UPDATE_REQUIRED,
    LOC_KEY_ERROR_WRONG_CREDENTIALS,
} from "./constants";
import { IErrorHandler } from "./IErrorHandler";

/**
 * Standardní implementace zachytává chyby a zobrazuje je uživateli pomocí MessageWindow.
 */
export class StandardErrorHandler implements IErrorHandler {
    private readonly _showFullErors: boolean;
    private _stopExceptionPropagation: boolean = false;
    private static _messageWindowTypeShow: { [name: string]: number } = {};
    private _exceptionTitle1: string | undefined;
    private _exceptionTitle2: string | undefined;
    protected _logger: ISectionLogger;
    protected _uiManager: IUIManagerWorker;

    constructor(showFullErors: boolean, uiManager: IUIManagerWorker, logger: ISectionLogger) {
        this._showFullErors = showFullErors;
        this._logger = logger;
        this._uiManager = uiManager;

        Localization.getTexts(LOC_KEY_ERROR_EVENT_ID, LOC_KEY_ERROR_NOTICE).then(r => {
            this._exceptionTitle1 = r.get(LOC_KEY_ERROR_EVENT_ID);
            this._exceptionTitle2 = r.get(LOC_KEY_ERROR_NOTICE);
        });
    }
    public activateAsyncGlobalErrorHandler(): void {
        self.addEventListener("unhandledrejection", event => {
            event.preventDefault();
            this._handleError(event.reason, false); // u asynchronních chyb se nedá vygenerovat callstack, proto se předává false
        });
    }

    public handleError(exception: any): void {
        this._handleError(exception, true);
    }

    protected _handleError(exception: any, showOuterCallStack: boolean): void {
        if (this._stopExceptionPropagation)
            return;

        if (exception instanceof UserAbortedException || exception instanceof CancelError)
            return;

        if (exception instanceof EInsufficientVersionException) {
            const msgWindow = new MessageWindow(new CryptoVersionInfoBuilder(exception));
            msgWindow.header = LOC_KEY_ERROR_UPDATE_REQUIRED;
            this._showMessage(msgWindow);
            return;
        } else if (exception instanceof NativeAppUnavailableException) {
            const msgWindow = new MessageWindow(new CryptoNativeAppUnavailableBuilder(exception));
            msgWindow.header = LOC_KEY_ERROR_MISSING_CNA_APP;
            this._showMessage(msgWindow);
            return;
        } else if (exception instanceof CryptoExtensionUnavailableException) {
            const msgWindow = new MessageWindow(new CryptoExtensionUnavailableBuilder(exception));
            msgWindow.header = LOC_KEY_ERROR_MISSING_CWE_EXTENSION;
            this._showMessage(msgWindow);
            return;
        }

        // zobrazeni uzivatelskych chyb
        if (exception instanceof EOWUserException && exception.resultMessages) {
            this._showResultMessages(exception);
            return;
        }

        if (exception instanceof InvalidConditionException) {
            const msgWindow = new MessageWindow();
            msgWindow.header = LOC_KEY_ERROR_INVALID_FILTER;

            if (exception.invalidValues.length > 0) {
                const forbiddenDataTypes = exception.invalidValues.filter(item => item.errorType === ConditionErrorType.forbiddenDataType);

                if (forbiddenDataTypes.length > 0) {
                    msgWindow.contentBuilder = new LocalizedStringBuilder(LOC_KEY_ERROR_INVALID_FILTER_ATTRIBUTE, [forbiddenDataTypes.map(item => item.attributeName).join(", ")]);
                } else {
                    msgWindow.contentBuilder = new LocalizedStringBuilder(LOC_KEY_ERROR_INVALID_EXTENDED_FILTER_VALUES, [exception.invalidValues.map(item => item.attributeName).join(", ")]);
                }
            } else {
                msgWindow.contentBuilder = new LocalizedStringBuilder(LOC_KEY_ERROR_INVALID_EXTENDED_FILTER);
            }

            this._showMessage(msgWindow);

            return;
        }

        if (exception instanceof EOWUnauthorizedAccess) {
            const msgWindow = new MessageWindow();
            msgWindow.contentBuilder = new StringBuilder(exception.message);
            this._showMessage(msgWindow);
            return;
        }

        if (exception instanceof DataSourceException) { // Zobrazuje se jen při developmentMode=false, jinak je zobrazen detail chyby
            const msgWindow = new MessageWindow();
            msgWindow.header = LOC_KEY_ERROR_NO_DATA;

            if (exception.isFiltered) {
                msgWindow.contentBuilder = new LocalizedStringBuilder(LOC_KEY_ERROR_CLEAR_AND_RETRY);
            } else {
                msgWindow.contentBuilder = new LocalizedStringBuilder(LOC_KEY_ERROR_CONTACT_ADMIN);
            }

            this._showMessage(msgWindow);

            return;
        }

        this._logger.log("⛔ Rozbilo se to");
        this._logger.error(exception);

        // Pokud došlo k padavé chybě, chceme skrýt všechny loadery
        while (this._uiManager.isLoading()) {
            this._uiManager.hideLoader();
        }

        // tyto vyjimky by se nemely zobrazovat v okne
        if (exception instanceof InvalidArgumentException || exception instanceof InvalidOperationException)
            return;

        if (exception instanceof EOWException && exception.message === "Object instance not found!")
            return;

        // Tato výjimka potlačuje vyskakování okna s tímto typem chyb. Přidáno dočasně na základě WI 33364 z důvodu momentálního velkého počtu těchto chyb. Do budoucna se plánuje chyby odstranit.
        if (exception instanceof TypeError && (
            exception.message.match(/^Cannot read property '.*' of (null|undefined)/) ||
            exception.message.match(/^Cannot read properties of (null|undefined) (.*)/) ||
            exception.message.match(/is (null|undefined)$/) ||
            exception.message.match(/^(null|undefined) is not an object/)
        )) {
            return;
        }

        const msg = new MessageWindow();
        msg.detailButton = true;
        msg.detailOpened = this._showFullErors;

        if (exception instanceof EOWSessionTimeOut) {
            this._redirectToLogoutPage(LogoutReason.sessionTimeOut); // Dojde k přesměrování na "speciální" stránku s textem "Byli jste odhlášení z aplikace."
        } else if (exception instanceof EOWKillSession) {
            this._redirectToLogoutPage(LogoutReason.multipleLogins); // Dojde k přesměrování na "speciální" stránku s textem "Byli jste odhlášení z důvodu přihlášení z jiného počítače."
        } else if (exception instanceof IDBException) {
            if (StandardErrorHandler._messageWindowTypeShow[IDBException.name])
                return;
            msg.header = LOC_KEY_ERROR_INDEXED_BD;
            msg.contentBuilder = new LocalizedStringBuilder(LOC_KEY_ERROR_RELOG_AND_RETRY);

            msg.otherButtonCaption = LOC_KEY_ERROR_LOGOUT;

            if (exception.innerError) {
                if (exception.innerError instanceof Error)
                    msg.detail = `${exception.innerError.name}: \n${exception.innerError.message}\n`;
            }
        } else if (exception instanceof EOWDmsUnavailable) {
            if (StandardErrorHandler._messageWindowTypeShow[EOWDmsUnavailable.name])
                return;

            msg.header = LOC_KEY_ERROR_STORAGE_UNAVAILABLE;
            msg.contentBuilder = new LocalizedStringBuilder(LOC_KEY_ERROR_FILES_UNAVAILABLE);
            msg.detailButton = false;
            StandardErrorHandler._messageWindowTypeShow[EOWDmsUnavailable.name] = 1;
        } else if (exception instanceof EDMSException) {
            if (StandardErrorHandler._messageWindowTypeShow[EDMSException.name])
                return;
            msg.header = LOC_KEY_ERROR_STORAGE_ERROR;
            if (exception.locKey)
                msg.contentBuilder = new LocalizedStringBuilder(exception.locKey);
            else
                msg.contentBuilder = new StringBuilder(exception.message);

            msg.detailButton = false;
            StandardErrorHandler._messageWindowTypeShow[EDMSException.name] = 1;
        } else if (exception instanceof EOWUnknownException) {
            msg.header = LOC_KEY_ERROR_GENERIC_FAIL;
            msg.contentBuilder = new LocalizedStringBuilder(LOC_KEY_ERROR_GENERIC_RETRY);
            msg.detailButton = true;
        } else if (exception instanceof EOWNoRightsToForm) {
            msg.header = LOC_KEY_ERROR_GENERIC_UNABLE;
            msg.contentBuilder = new LocalizedStringBuilder(LOC_KEY_ERROR_NO_RIGHTS_FORM);
            msg.detailButton = false;
        } else if (exception instanceof EOWNoFormNameException) {
            msg.header = LOC_KEY_ERROR_FORM_DEFINITION_NOT_FOUND;
            msg.contentBuilder = new LocalizedStringBuilder(LOC_KEY_ERROR_MISSING_FORM_NAME);
            msg.detailButton = false;
        } else if (exception instanceof EOWFormDefinitionNotFoundException) {
            msg.header = LOC_KEY_ERROR_FORM_DEFINITION_NOT_FOUND;
            msg.detailButton = false;
            msg.contentBuilder = new LocalizedStringBuilder(LOC_KEY_ERROR_UNABLE_TO_OPEN_FORM, [exception.FormName]);
        } else if (exception instanceof EOWNoImplicitFormForClassException) {
            msg.header = LOC_KEY_ERROR_FORM_DEFINITION_NOT_FOUND;
            msg.detailButton = false;
            msg.contentBuilder = new LocalizedStringBuilder(LOC_KEY_ERROR_NO_ASSOCIATED_FORM);
        } else if (exception instanceof EOWLogonError) {
            msg.header = LOC_KEY_ERROR_GENERIC_UNABLE;
            msg.contentBuilder = new LocalizedStringBuilder(LOC_KEY_ERROR_WRONG_CREDENTIALS);
            msg.detailButton = false;
        } else if (exception instanceof EOWUserException) {
            msg.header = LOC_KEY_ERROR_GENERIC_UNABLE;
            msg.contentBuilder = new StringBuilder(exception.message);
            msg.detailButton = false;
        } else if (exception instanceof EOWRecordPermissionException) {
            msg.header = LOC_KEY_ERROR_INSUFFICIENT_RIGHTS;
            msg.detailButton = false;
        } else if (exception instanceof EOWLocalizableException) {
            msg.header = exception.locKeyHeader;
            msg.detailButton = false;
            msg.contentBuilder = new LocalizedStringBuilder(exception.locKeyBody);
        } else if (exception instanceof EReadOnlyAttribute) {
            msg.header = LOC_KEY_ERROR_GENERIC_UNABLE;
            msg.contentBuilder = new LocalizedStringBuilder(LOC_KEY_ERROR_UNABLE_TO_MODIFY, [exception.attributeName]);
            msg.detailButton = false;
        } else if (exception instanceof EReadOnlyMethod) {
            msg.header = LOC_KEY_ERROR_GENERIC_UNABLE;
            msg.contentBuilder = new LocalizedStringBuilder(LOC_KEY_ERROR_INSUFFICIENT_RIGHTS_RUN_ACTION);
            msg.detailButton = false;
        } else if (exception instanceof EOWRunMethodInsufficientRightsException) {
            msg.header = LOC_KEY_ERROR_UNABLE_TO_RUN_ACTION;
            msg.contentBuilder = new LocalizedStringBuilder(LOC_KEY_ERROR_CONDITION_NOT_MET);
            msg.detailButton = false;
        } else if (exception instanceof EOWValidateException) {
            const msgs: IUserMsgsData[] = [];
            for (const valErr of exception.validationErrors)
                msgs.push({ objectIdentification: exception.name, messages: [{ message: valErr.errorMessage, type: ResultMessageType.error }] });
            msg.contentBuilder = new UserMsgsBuilder(msgs);
            msg.detailButton = false;
        } else if (exception instanceof EOWException) {
            if (exception.httpErrorCode === 423) {
                this._stopExceptionPropagation = true;
                msg.header = LOC_KEY_ERROR_LOGGED_OUT;
                msg.contentBuilder = new LocalizedStringBuilder(LOC_KEY_ERROR_SECURITY_TOKEN_EXPIRED);
            } else {
                msg.header = LOC_KEY_ERROR_GENERIC_FAIL;
                msg.contentBuilder = new LocalizedStringBuilder(LOC_KEY_ERROR_SERVER_UNAVAILABLE);
            }
        } else if (exception instanceof NativeAppException) {
            msg.header = LOC_KEY_ERROR_UNABLE_TO_CREATE_DIGITAL_SIGNATURE;
            if (exception instanceof NativeAppExitedException)
                msg.contentBuilder = new LocalizedStringBuilder(LOC_KEY_ERROR_CNA_EXITED);
            else if (exception instanceof NativeAppSelectionParamsException)
                msg.contentBuilder = new LocalizedStringBuilder(LOC_KEY_ERROR_CNA_SELECTIONPARAMS);
            else if (exception instanceof NativeAppNoCertificateFoundException)
                msg.contentBuilder = new LocalizedStringBuilder(LOC_KEY_ERROR_CNA_NO_CERTIFICATE_FOUND);
            else if (exception instanceof NativeAppTooManyCertificatesFoundException)
                msg.contentBuilder = new LocalizedStringBuilder(LOC_KEY_ERROR_CNA_TOO_MANY_CERTIFICATES_FOUND);
            else if (exception instanceof NativeAppBulkException) { //obsahuje v sobě výjimky pro jednotlivé podepsané streamy. Bude tak možné vypsat chyby pro konkrétní podepsané streamy.
                //v případě potřeby pro sestavení zprávy vytvořit samostatný data builder
                const msgs: IUserMsgsData[] = [];
                for (const innerEx of exception)
                    msgs.push({ objectIdentification: innerEx.name, messages: [{ message: innerEx.message, type: ResultMessageType.error }] });
                msg.contentBuilder = new UserMsgsBuilder(msgs);
            } else
                msg.contentBuilder = new StringBuilder(exception.message);
            msg.detailButton = false;
        } else {
            msg.header = LOC_KEY_ERROR_GENERIC_FAIL;
            msg.type = MessageType.debug;
            msg.contentBuilder = new LocalizedStringBuilder(LOC_KEY_ERROR_GENERIC_FAIL_RETRY);
        }

        msg.type = MessageType.message;
        if (exception instanceof EOWException) {
            let detail = this._buildErrorTitle(exception);
            if (exception.logExceptionID)
                detail += this._buildExceptionIdInfo(exception);
            if (exception.serverStackTrace)
                detail += this._buildServerStacktraceInfo(exception.serverStackTrace);
            detail += this._buildClientInnerStacktraceInfo(exception.stack);
            if (showOuterCallStack)
                detail += this._buildClientOuterStacktraceInfo(new Error().stack);
            if (exception.request && this._showFullErors /* zatím jen v developmentMode */)
                detail += this._buildRequestInfo(exception);
            msg.detail = detail;
        } else if (exception?.stack)
            msg.detail = this._buildErrorInfo(exception);
        else
            msg.detail = JSON.stringify(exception);

        this._showMessage(msg).then((res) => {
            if ((exception instanceof IDBException && res.result === MessageButtonType.other) ||
                this._stopExceptionPropagation) {
                location.assign(`./${location.search}`); // `
            } else if (exception instanceof EOWDmsUnavailable || exception instanceof EDMSException) {
                StandardErrorHandler._messageWindowTypeShow[exception.constructor.name] = 0;
            }
        }).catch((err) => {
            this.handleError(err);
        });
    }

    protected _buildErrorInfo(exception: any): string {
        return `${exception.name}: \n${exception.message}\n\nStack:\n------\n${exception.stack}`; //`
    }

    protected _buildErrorTitle(exception: EOWException): string {
        return `${exception.name}: \n${exception.message}\n`; //`
    }

    protected _buildClientInnerStacktraceInfo(stack: string): string {
        return `\nClient inner callstack:\n-----------------------\n${stack}\n`; //`
    }

    protected _buildClientOuterStacktraceInfo(stack: string): string {
        // odstraní zbytečnou prázdnou error message na začátku stacku (jen ve Chromiu, jinde se ke stacku název chyby nepřidává)
        if (stack.startsWith(Error.name))
            stack = stack.substr(Error.name.length + 1);

        return `\nClient outer callstack:\n-----------------------\n${stack}\n`; //`
    }

    protected _buildServerStacktraceInfo(stack: string): string {
        // odfiltruje zbytečné dodatečné informace, které jen znepřehledňují výpis
        const indexOfEnd = stack.indexOf("--- End of stack trace");
        if (indexOfEnd && indexOfEnd > 0)
            stack = stack.substr(0, indexOfEnd);

        stack = stack.replace(/(\r\n|\r|\n){2,}/g, "$1"); // regulár nahradí zbytečná vícenásobná odřádkování
        return `\nServer callstack:\n-----------------\n${stack}\n`; //`
    }

    protected _buildExceptionIdInfo(exception: EOWException): string {
        return `${this._exceptionTitle1} ${exception.logExceptionID}\n${this._exceptionTitle2}\n`; //`
    }

    protected _buildRequestInfo({ request }: EOWException): string {
        const url = new URL(request.url);

        let result = "\nHTTP Request:\n-------------\n";
        result += `${request.requestMethod} ${url.origin}${url.pathname}\n`; //`

        const queryParams = [...url.searchParams];
        if (queryParams.length > 0) {
            result += "\nRequest Params:\n";
            for (const [paramName, paramValue] of queryParams)
                result += `${paramName}: ${decodeURIComponent(decodeURIComponent(paramValue))}\n`; //`
        }

        const headers = this._getRequestHeaders(request.headers);
        if (headers.length > 0) {
            result += "\nRequest Headers:\n";
            for (const [headerName, headerValue] of headers)
                result += `${headerName}: ${decodeURIComponent(decodeURIComponent(headerValue))}\n`; //`
        }

        if (request.data) {
            result += "\nRequest Payload:\n";
            if (typeof request.data === "string")
                try {
                    // pokud je v datech json, tak ho zkusíme zparsovat a naformátovat pro lepší zobrazení
                    result += JSON.stringify(JSON.parse(request.data), undefined, 2);
                } catch {
                    result += request.data;
                }
            else
                result += JSON.stringify(request.data, undefined, 2);
        }
        return result;
    }

    private _getRequestHeaders(originalHeaders: Iterable<[string, string]> | { [name: string]: string | boolean }): readonly [string, string][] {
        if (!originalHeaders)
            return [];

        // authid se z výstupu radši odfiltruje, aby ho někdo zbytečně někomu nenasdílel a nevytvořil tak na sebe možnost útoku

        if (Symbol.iterator in originalHeaders)
            return [...originalHeaders as Iterable<[string, string]>].filter(([key]) => key !== "authid");

        return Object.entries(originalHeaders).filter(([key]) => key !== "authid");
    }

    protected _showMessage(msg: MessageWindow): Promise<MessageButtonResult> {
        return msg.show();
    }

    protected async _showResultMessages(exception: EOWUserException): Promise<void> {
        const messages = exception.resultMessages;

        if (!messages || messages.length === 0)
            return;

        await MessageWindow.showAlert(new UserMsgsBuilder([new UserMsgsData(messages)]));
    }

    /**
     * Přesměruje na odhlašovací stránku s Query stringem obsahujícím důvod odhlášení a případně parametry formuláře
     * @param logoutReason důvod odhlášení
     */
    protected _redirectToLogoutPage(logoutReason: LogoutReason): void {
        top.location.assign(`logout?reason=${logoutReason}${this._getFormParamsFromQueryString()}`);
    }

    /**
     * Získá z Query stringu parametry o formuláři
     */
    private _getFormParamsFromQueryString(): string {
        const queryString = top.location.search;
        return queryString.length > 0 ? "&" + queryString.substring(1) : "";
    }
}

/**
 * Enum obsahující důvody odhlášení z aplikace
 * Nachází se i v umístění "resources\static\js\logout.js", při změně je nutné ho v uvedeném umístění synchronizovat
 */
enum LogoutReason {
    sessionTimeOut = "session-timeout",
    multipleLogins = "multiple-logins",
}
